import React, { useEffect, useMemo, useState } from "react";

import { templates } from "@reactivated";

import { RichText } from "../../../common/RichText";
import { getOptionValueHash } from "../../../utils/catalogue";
import { ShopTileType } from "../ShopCompareTypes";
import { ShopTile } from "../ShopTile";

import styles from "../ShopCompare/index.module.scss";

interface IShopCompareGridProps {
    value: templates.ShopCompareGridTemplate;
}

export const ShopCompareGrid = (props: IShopCompareGridProps) => {
    const tileset = props.value.tileset;

    const tiles = tileset?.shop_tiles?.value ?? [];

    const allSizeOptions = useMemo(() => {
        return tiles
            .map((shopTileObject) => {
                const shopTile = (shopTileObject.value as ShopTileType)
                    .shop_tile;
                return Array.isArray(shopTile?.size_options)
                    ? shopTile?.size_options
                    : undefined;
            })
            .filter((arr): arr is string[] => Array.isArray(arr));
    }, [tiles]);

    const availableSizes = useMemo(() => {
        if (allSizeOptions.length === 0) return [] as string[];
        return allSizeOptions.reduce((acc, curr) => {
            if (acc.length === 0) return curr;
            return acc.filter((size) => curr.includes(size));
        }, allSizeOptions[0]);
    }, [allSizeOptions]);

    const tileCount = tiles.length;
    const manyTiles = tileCount > 5;

    const gridClass = useMemo(() => {
        if (tileCount >= 6) return styles.cols6;
        if (tileCount === 5) return styles.cols5;
        if (tileCount === 4) return styles.cols4;
        if (tileCount === 3) return styles.cols3;
        return "";
    }, [tileCount]);

    const defaultSelected = useMemo(() => {
        if (availableSizes.length > 0) {
            return availableSizes.includes("Queen")
                ? "Queen"
                : availableSizes[0];
        }
        return "Queen";
    }, [availableSizes]);

    const [selectedSize, setSelectedSize] = useState<string>(defaultSelected);

    useEffect(() => {
        setSelectedSize(defaultSelected);
    }, [defaultSelected]);

    const selectID = "size-selector";
    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
    };
    const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSize(e.target.value);
    };

    if (!tileset) {
        return null;
    }

    return (
        <section className={styles.shopCompareContainer}>
            <RichText
                className={styles.header}
                html={props.value.header_copy}
            />
            {tileset.show_size_selector && availableSizes.length > 0 && (
                <form className={styles.sizeSelector} onSubmit={onSubmit}>
                    <label htmlFor={selectID}>Price by mattress size</label>
                    <select
                        id={selectID}
                        aria-label="Select mattress size"
                        className="al-compareresults-selectsize"
                        onChange={onSelectChange}
                        value={selectedSize}
                    >
                        {availableSizes.map((option) => {
                            return (
                                <option
                                    key={getOptionValueHash(option)}
                                    value={option}
                                >
                                    {option}
                                </option>
                            );
                        })}
                    </select>
                </form>
            )}
            <div className={`${styles.shopTileContainer} ${gridClass}`}>
                {tiles.map((tile, index) => {
                    const tileId = tile.value.shop_tile?.id || `tile-${index}`;
                    return (
                        <div key={tileId}>
                            <ShopTile
                                value={tile.value}
                                tilesetId={tileset.id}
                                compactPricing={manyTiles}
                            />
                        </div>
                    );
                })}
            </div>
            {props.value.footer_copy &&
                props.value.footer_copy.trim() !== "" && (
                    <div className={styles.footer}>
                        <RichText html={props.value.footer_copy} />
                    </div>
                )}
        </section>
    );
};
